// routes/_authenticated/documents/$type/$cnpj/$year/$referenceDate/$idDoc/index.tsx
// — document detail route (no version). Reads all four document
// identifiers from the path: type, cnpj, year, referenceDate, idDoc.
// Nothing in the search string.
//
// ponytail: the per-route path-validation lives at
// `@/features/documents/route-params.ts` so the `$versao` sibling route
// and the unit test (`parse-params.test.ts`) share the exact same accepted
// shape. The route file stays a thin shell — validate, dispatch to the
// matching `<XxxDocumentPage />`, optionally show the missing-key card.
import { createFileRoute, Link } from "@tanstack/react-router";

import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import {
  CgvnDocumentPage,
  DfpDocumentPage,
  FcaDocumentPage,
  FreDocumentPage,
  IpeDocumentPage,
  ItrDocumentPage,
  UnsupportedDocumentPage,
  VlmoDocumentPage,
} from "@/features/documents";
import type { DocumentKey, DocumentType } from "@/features/documents/api";
import { EMPTY_PARAMS, parseRouteParams } from "@/features/documents/route-params";
import { BackButton } from "@/routes/_authenticated/documents/$type/-back-button";

const SUPPORTED_TYPES: DocumentType[] = ["FRE", "ITR", "DFP", "CGVN", "FCA", "VLMO", "IPE"];

function normalizeType(raw: string): DocumentType {
  const t = raw.toUpperCase() as DocumentType;
  return SUPPORTED_TYPES.includes(t) ? t : "FRE";
}

export const Route = createFileRoute(
  "/_authenticated/documents/$type/$cnpj/$year/$referenceDate/$idDoc/",
)({
  component: DocumentDetailRoute,
});

function DocumentDetailPage({ type, docKey }: { type: DocumentType; docKey: DocumentKey }) {
  if (type === "ITR") return <ItrDocumentPage docKey={docKey} />;
  if (type === "DFP") return <DfpDocumentPage docKey={docKey} />;
  if (type === "CGVN") return <CgvnDocumentPage docKey={docKey} />;
  if (type === "FCA") return <FcaDocumentPage docKey={docKey} />;
  if (type === "VLMO") return <VlmoDocumentPage docKey={docKey} />;
  if (type === "IPE") return <IpeDocumentPage docKey={docKey} />;
  return <FreDocumentPage docKey={docKey} />;
}

function DocumentDetailRoute() {
  const params = Route.useParams();
  const parsed = parseRouteParams(params);
  const ready = parsed !== EMPTY_PARAMS;
  const type = ready ? normalizeType(parsed.type) : null;

  return (
    <PageShell
      title={type ? `Documento ${type}` : "Documento"}
      description="Detalhe completo do formulário entregue à CVM."
      actions={ready ? <BackButton /> : null}
    >
      {!ready ? (
        <MissingSegmentCard />
      ) : type && SUPPORTED_TYPES.includes(type) ? (
        <DocumentDetailPage
          type={type}
          docKey={{
            year: parsed.year,
            cnpj: parsed.cnpj,
            referenceDate: parsed.referenceDate,
            idDoc: parsed.idDoc,
          }}
        />
      ) : (
        <UnsupportedDocumentPage type={(type ?? "FRE") as DocumentType} />
      )}
    </PageShell>
  );
}

// ponytail: BackButton is shown only on the success path. The missing-segment
// card and the unsupported-type placeholder already render their own return
// affordances, so layering a third button on those states would compete with
// the existing copy. The component itself lives in back-button.tsx so its
// resolve logic can be unit-tested without spinning up a full router
// provider.

function MissingSegmentCard() {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-md border border-dashed py-12 text-center"
      data-testid="document-missing-segment"
    >
      <div className="text-sm font-medium">URL de documento incompleta</div>
      <div className="text-muted-foreground max-w-md text-xs">
        Esta rota exige <code>type</code>, <code>cnpj</code>, <code>year</code>,{" "}
        <code>referenceDate</code> e <code>idDoc</code> como segmentos do caminho. Volte ao
        dashboard e clique em um filing recente.
      </div>
      <Button asChild variant="outline" size="sm">
        <Link to="/dashboard">Voltar ao dashboard</Link>
      </Button>
    </div>
  );
}
